import * as React from 'react'
import { connect } from 'react-redux'

import PaginateOffers from 'embed/components/PaginateOffers'
import { CreditDisclosure } from '../CreditDisclosure'
import { ConnectedCreditOfferListContainer } from '../CreditOfferList/container'

import { creditCardOfferPreviewSelected, fetchCreditCardOffers } from 'common/actions'
import { filteredRatesSelector } from 'embed/selectors'
import { brandSelector } from 'embed/selectors/config'
import { MultiProductDispatch, OfferOverflowList } from 'embed/constants'
import { CreditOffer } from 'embed/constants/models'
import { MultiProductState } from 'embed/reducers'
import { uiPreviewIsInitializingSelector } from 'embed/selectors/ui'
import { Brand, ConfigKeys, CreditCardFlow, Product, ProductResponseKeys } from 'even-ts-static'

interface DispatchProps {
  dispatchFetchCreditCardOffers: () => void
  dispatchOfferPreviewSelected: (offer: CreditOffer, openInNewTab?: boolean, flow?: boolean) => void
  dispatchOfferPreviewSelectedSecondary: (offer: CreditOffer, openInNewTab?: boolean, flow?: boolean) => void
}

interface StoreProps {
  brand: Brand
  creditOffers: CreditOffer[]
  leadUuid: string
  loadingPredicate: boolean
  offerOverflow: string | undefined
  pageSize: number | undefined
  rateTableUuid: string
  resultsUuid: string | undefined
}

type Props = DispatchProps & StoreProps

const CreditCardHybridOfferListContainer: React.FunctionComponent<Props> = ({
  brand,
  creditOffers,
  dispatchFetchCreditCardOffers,
  dispatchOfferPreviewSelected,
  dispatchOfferPreviewSelectedSecondary,
  offerOverflow,
  pageSize,
  resultsUuid,
  ...other
}) => {
  React.useEffect(() => {
    if (resultsUuid === undefined) {
      dispatchFetchCreditCardOffers()
    }
  }, [])

  const onPreviewSelected = (offer: CreditOffer) => dispatchOfferPreviewSelected(offer, false)
  const onPreviewSelectedSecondary = (offer: CreditOffer) => dispatchOfferPreviewSelectedSecondary(offer, false)

  const offerListRef = React.useRef(null)
  return (
    <>
      <PaginateOffers
        enabled={offerOverflow === OfferOverflowList.PAGINATE}
        offerListRef={offerListRef}
        offers={creditOffers}
        pageSize={pageSize}
      >
        {({ offersPage, paginationControls }) => (
          <div style={{ marginTop: '8px' }}>
            <ConnectedCreditOfferListContainer
              creditOffers={offersPage as CreditOffer[]}
              handleOfferCta={onPreviewSelected}
              handleOfferCtaSecondary={onPreviewSelectedSecondary}
              offerListRef={offerListRef}
              {...other}
            />
            {paginationControls}
            <CreditDisclosure brand={brand} />
          </div>
        )}
      </PaginateOffers>
    </>
  )
}

export const mapStateToProps = (state: MultiProductState): StoreProps => ({
  brand: brandSelector(state),
  creditOffers: filteredRatesSelector(state, {
    offersPath: `${Product.CreditCard}.${ProductResponseKeys[Product.CreditCard]}Offers`,
    product: Product.CreditCard,
  }),
  leadUuid: state.rates.credit_card.leadUuid,
  loadingPredicate: uiPreviewIsInitializingSelector(state),
  offerOverflow: state.config.offer_overflow,
  pageSize: state.config.page_size,
  rateTableUuid: state.rates.credit_card.uuid,
  resultsUuid: state.config[ConfigKeys.ResultsUuid],
})

const mapDispatchToProps = (dispatch: MultiProductDispatch): DispatchProps => ({
  dispatchFetchCreditCardOffers: () => dispatch(fetchCreditCardOffers()),
  dispatchOfferPreviewSelected: (offer: CreditOffer, openInNewTab?: boolean) =>
    dispatch(creditCardOfferPreviewSelected(offer, { openInNewTab, flow: CreditCardFlow.PreSelect })),
  dispatchOfferPreviewSelectedSecondary: (offer: CreditOffer, openInNewTab?: boolean) =>
    dispatch(creditCardOfferPreviewSelected(offer, { openInNewTab, flow: CreditCardFlow.Redirect })),
})

export const CreditCardHybridOfferList = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreditCardHybridOfferListContainer)
